<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div class="d-flex align-center">
                    <v-avatar v-if="device?.sku_picture" :image="device.sku_picture" size="72"
                              rounded="sm" class="me-4" @error="$event.target.style.display='none'"/>
                    <div>
                        <h1 class="text-left">Device Details<small>(#{{ device?.id ?? '' }})</small></h1>
                        <div v-if="device?.sku_name" class="text-subtitle-1 text-medium-emphasis">
                            {{ device.sku_name }}
                        </div>
                    </div>
                </div>
                <v-btn-group v-if="device && user.type !== 'company'">
                    <v-btn color="warning" prepend-icon="mdi-pencil" size="small" @click="editDialogSwitch = true">
                        Edit
                    </v-btn>
                    <v-menu>
                        <template v-slot:activator="{ props }">
                            <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text" size="small"/>
                        </template>
                        <v-list density="compact">
                            <v-list-item title="Send Command" prepend-icon="mdi-console"
                                         @click="commandDialogSwitch = true"/>
                            <v-list-item title="OTA Status" prepend-icon="mdi-cloud-download"
                                         @click="otaStatusDialogSwitch = true"/>
                            <v-list-item title="Message Logs" prepend-icon="mdi-message-text-outline"
                                         @click="openMessageLogs"/>
                            <template v-if="user.super">
                                <v-divider class="my-1"/>
                                <v-list-item title="Repair License" prepend-icon="mdi-key-chain"
                                             @click="repairLicense"/>
                                <v-list-item title="Service Pin" prepend-icon="mdi-shield-key"
                                             @click="servicePinDialogSwitch = true"/>
                            </template>
                        </v-list>
                    </v-menu>
                </v-btn-group>
            </v-col>
        </v-row>

        <v-row>
            <!-- Info Panel + Map side by side -->
            <v-col cols="12" md="9">
                <div class="pa-3" style="border: 1.5px solid #E0E0E0; border-radius: 8px; background-color: #FAFAFA;">
                    <!-- Asset + Company + OTA badges row -->
                    <div v-if="device" class="d-flex align-center ga-2 mb-2 flex-wrap">
                        <v-chip v-if="device.asset_label" size="small" color="primary" variant="tonal"
                                prepend-icon="mdi-car" :to="device.asset_id ? `/assets/${device.asset_id}` : undefined">
                            {{ device.asset_label }}
                        </v-chip>
                        <v-chip v-else size="small" color="grey" variant="outlined" prepend-icon="mdi-car-off">
                            No Asset Assigned
                        </v-chip>
                        <v-chip v-if="device.company_name" size="small" variant="tonal" prepend-icon="mdi-domain">
                            {{ device.company_name }}
                        </v-chip>
                        <v-chip v-if="device.device_status?.ota_status > 0" size="small"
                                :color="otaColor(device.device_status.ota_status)" variant="tonal"
                                prepend-icon="mdi-cloud-download">
                            OTA: {{ otaLabel(device.device_status.ota_status) }}
                            <span v-if="device.device_status.ota_progress > 0" class="ms-1">
                                {{ device.device_status.ota_progress }}%
                            </span>
                        </v-chip>
                        <v-chip v-if="device.bypass_payment" size="small" color="grey" variant="tonal"
                                prepend-icon="mdi-shield-check">
                            Bypass Payment
                        </v-chip>
                        <v-chip v-else-if="device.subscription_plan_id" size="small"
                                :color="subscriptionChipColor(device.subscription_end_date)" variant="tonal"
                                prepend-icon="mdi-credit-card-clock">
                            {{ subscriptionChipLabel(device.subscription_end_date) }}
                        </v-chip>
                    </div>

                    <v-row dense>
                        <!-- Device Hardware -->
                        <v-col cols="12" md="3">
                            <div class="text-caption font-weight-bold text-grey mb-1">DEVICE</div>
                            <v-table density="compact">
                                <tbody class="text-start">
                                <tr>
                                    <td class="text-grey" style="width:100px">Serial</td>
                                    <td>{{ deviceDetails.slno || '-' }}</td>
                                </tr>
                                <tr>
                                    <td class="text-grey">IMEI</td>
                                    <td>{{ deviceDetails.imei || '-' }}</td>
                                </tr>
                                <tr>
                                    <td class="text-grey">SKU</td>
                                    <td>{{ deviceDetails.sku_name || deviceDetails.sku || '-' }}</td>
                                </tr>
                                <tr>
                                    <td class="text-grey">Status</td>
                                    <td>{{ deviceDetails.status || '-' }}</td>
                                </tr>
                                <tr>
                                    <td class="text-grey">ICCID</td>
                                    <td class="text-caption">{{ deviceDetails.iccid || '-' }}</td>
                                </tr>
                                </tbody>
                            </v-table>
                        </v-col>

                        <!-- Firmware -->
                        <v-col cols="12" md="3">
                            <div class="text-caption font-weight-bold text-grey mb-1">FIRMWARE</div>
                            <v-table density="compact">
                                <tbody class="text-start">
                                <tr v-if="deviceDetails.device_status?.version_mod1">
                                    <td class="text-grey" style="width:100px">App</td>
                                    <td>{{ deviceDetails.device_status.version_mod1 }}</td>
                                </tr>
                                <tr v-if="deviceDetails.device_status?.version_mod2">
                                    <td class="text-grey">BT/WiFi</td>
                                    <td>{{ deviceDetails.device_status.version_mod2 }}</td>
                                </tr>
                                <tr v-if="deviceDetails.device_status?.version_mod3">
                                    <td class="text-grey">Modem</td>
                                    <td>{{ deviceDetails.device_status.version_mod3 }}</td>
                                </tr>
                                </tbody>
                            </v-table>
                        </v-col>

                        <!-- Connectivity -->
                        <v-col cols="12" md="3">
                            <div class="text-caption font-weight-bold text-grey mb-1">CONNECTIVITY</div>
                            <v-table density="compact">
                                <tbody class="text-start">
                                <tr>
                                    <td class="text-grey" style="width:100px">Signal</td>
                                    <td>{{ deviceDetails.signal_bars ?? 0 }} bars
                                        <span v-if="device?.device_status?.rsrp" class="text-caption text-grey ms-1">
                                            RSRP {{ device.device_status.rsrp }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-grey">GPS</td>
                                    <td>
                                        {{ deviceDetails.device_status?.satellites ?? '-' }} sats,
                                        {{ fixTypes[deviceDetails.device_status?.fix_type ?? 0] }} fix
                                        <span v-if="deviceDetails.device_status?.accuracy" class="text-caption text-grey ms-1">
                                            ±{{ deviceDetails.device_status.accuracy }}m
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-grey">Last Comm</td>
                                    <td class="text-caption">{{ deviceDetails.device_status?.updated_at ? formatDateTime(deviceDetails.device_status.updated_at) : '-' }}</td>
                                </tr>
                                </tbody>
                            </v-table>
                        </v-col>

                        <!-- Subscription -->
                        <v-col cols="12" md="3">
                            <div class="text-caption font-weight-bold text-grey mb-1">SUBSCRIPTION</div>
                            <v-table density="compact">
                                <tbody class="text-start">
                                <tr>
                                    <td class="text-grey" style="width:100px">Plan</td>
                                    <td>{{ device?.plan_name || (device?.subscription_plan_id ? '#' + device.subscription_plan_id : 'None') }}</td>
                                </tr>
                                <tr>
                                    <td class="text-grey">Expiry</td>
                                    <td :class="subscriptionExpiryClass(device?.subscription_end_date)">
                                        {{ device?.subscription_end_date || '-' }}
                                    </td>
                                </tr>
                                <tr v-if="device?.bypass_payment">
                                    <td class="text-grey">Billing</td>
                                    <td class="text-grey-darken-1">Bypass</td>
                                </tr>
                                </tbody>
                            </v-table>
                        </v-col>

                        <!-- Sensors -->
                        <v-col cols="12" md="3">
                            <div class="text-caption font-weight-bold text-grey mb-1">SENSORS</div>
                            <v-table density="compact">
                                <tbody class="text-start">
                                <tr>
                                    <td class="text-grey" style="width:100px">Battery</td>
                                    <td>{{ deviceDetails.voltage1 }} / {{ deviceDetails.voltage2 }}</td>
                                </tr>
                                <tr v-if="deviceDetails.device_status?.temperature_onboard != null">
                                    <td class="text-grey">Temp</td>
                                    <td>{{ deviceDetails.device_status.temperature_onboard }}°C</td>
                                </tr>
                                <tr>
                                    <td class="text-grey">Odometer</td>
                                    <td>{{ device?.device_status?.odometer ? strOperations().unitifyDistance(device.device_status.odometer, 'distance', 'km') : '-' }}</td>
                                </tr>
                                <tr v-if="deviceDetails.device_status?.gps_speed != null">
                                    <td class="text-grey">Speed</td>
                                    <td>GPS: {{ deviceDetails.device_status.gps_speed }}
                                        <span v-if="deviceDetails.device_status?.obd_speed"> / OBD: {{ deviceDetails.device_status.obd_speed }}</span>
                                    </td>
                                </tr>
                                </tbody>
                            </v-table>
                        </v-col>
                    </v-row>
                </div>
            </v-col>

            <!-- Location Map -->
            <v-col v-if="device?.device_status?.latitude && device?.device_status?.longitude"
                   cols="12" md="3">
                <div style="height: 260px; position: relative; border-radius: 8px; overflow: hidden; border: 1px solid #e0e0e0;">
                    <l-map ref="locationMap" :zoom="14"
                           :center="[device.device_status.latitude, device.device_status.longitude]"
                           :options="{zoomControl: false, attributionControl: false}"
                           @ready="onLocationMapReady">
                        <l-tile-layer :url="$mapTiles.street"/>
                        <l-marker :lat-lng="[device.device_status.latitude, device.device_status.longitude]">
                            <l-icon :icon-size="[30, 30]">
                                <i class="mdi mdi-map-marker" style="font-size: 30px; color: #1976D2;"></i>
                            </l-icon>
                        </l-marker>
                    </l-map>
                    <div style="position: absolute; bottom: 8px; right: 8px; z-index: 1000;" class="d-flex ga-1">
                        <v-tooltip text="Copy Location" location="top">
                            <template #activator="{ props: tp }">
                                <v-btn v-bind="tp" icon="mdi-content-copy" size="x-small" color="white"
                                       variant="elevated" density="compact"
                                       @click="copyToClipboard(`https://www.google.com/maps?q=${device.device_status.latitude},${device.device_status.longitude}`)"/>
                            </template>
                        </v-tooltip>
                        <v-tooltip text="Open in Google Maps" location="top">
                            <template #activator="{ props: tp }">
                                <v-btn v-bind="tp" icon="mdi-google-maps" size="x-small" color="white"
                                       variant="elevated" density="compact"
                                       @click="openGoogleMaps"/>
                            </template>
                        </v-tooltip>
                    </div>
                </div>
                <div class="text-caption text-center mt-1 text-grey">
                    {{ device.device_status.latitude }}, {{ device.device_status.longitude }}
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-tabs v-model="activeTab"
                        grow
                        @update:model-value="updateTab">
                    <v-tab
                        v-for="tab in tabs"
                        :loading="!device"
                        :key="tab.value">{{ tab.title }}
                    </v-tab>
                </v-tabs>

                <v-tabs-window v-model="activeTab">
                    <v-tabs-window-item
                        v-for="tab in tabs"
                        :key="tab.value">
                        <v-card flat>
                            <v-card-text v-if="activeTab === 0">
                                <v-table density="compact" hover>
                                    <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Type</th>
                                        <th>Title</th>
                                        <th>Description</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(notification, index) in notifications"
                                        v-if="!notificationsProcessing && notifications.length" :key="index"
                                        class="text-left">
                                        <td>{{ formatDateTime(notification.event_timestamp) }}</td>
                                        <td>
                                            <v-icon :icon="alertIcon(notification)" :color="alertColor(notification)"
                                                    size="x-small"/>
                                            {{ alertLabel(notification) }}
                                        </td>
                                        <td>{{ notification.title }}</td>
                                        <td>{{ notification.description }}</td>
                                    </tr>
                                    <tr v-else>
                                        <td v-if="notificationsProcessing" colspan="4">
                                            <v-progress-circular color="primary" indeterminate/>
                                        </td>
                                        <td v-else colspan="4">No notification found</td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <v-pagination v-if="notificationsTotalPages > 1"
                                              v-model="notificationsCurrentPage" :length="notificationsTotalPages"
                                              class="py-0 pt-2"
                                              density="compact"
                                              @click="fetchNotifications"/>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 1">
                                <v-table density="compact" hover>
                                    <thead>
                                    <tr>
                                        <th></th>
                                        <th>Component/ Module Name</th>
                                        <th>Version</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="ota_component in deviceDetails.ota_device_components"
                                        v-if="deviceDetails.ota_device_components"
                                        class="text-left">
                                        <td>Version {{ ota_component.module_index }}</td>
                                        <td>{{ ota_component.name }}</td>
                                        <td>{{
                                                deviceDetails.device_status?.[`${'version_mod' + ota_component.module_index}`]
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td colspan="3">No record found</td>
                                    </tr>
                                    </tbody>
                                </v-table>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 2">
                                <v-table density="compact" hover>
                                    <thead>
                                    <tr>
                                        <th-btn :sorted-column="messagesSortedColumn" column="id" sortable title="ID"
                                                @sort="messagesSort"/>
                                        <th-btn :sorted-column="messagesSortedColumn" column="event_time" sortable
                                                title="Timestamp" @sort="messagesSort"/>
                                        <th-btn :sorted-column="messagesSortedColumn" column="trigger_event" sortable
                                                title="Triggered By" @sort="messagesSort"/>
                                        <th-btn :sorted-column="messagesSortedColumn" column="heading" sortable
                                                title="Speed" @sort="messagesSort"/>
                                        <th-btn :sorted-column="messagesSortedColumn" column="voltage1" sortable
                                                title="Voltage" @sort="messagesSort"/>
                                        <th-btn :sorted-column="messagesSortedColumn" column="rssi" sortable
                                                title="Signal Bars/RSSI" @sort="messagesSort"/>
                                        <th>Lat, Lon</th>
                                        <th-btn :sorted-column="messagesSortedColumn" column="satellites" sortable
                                                title="Satellites" @sort="messagesSort"/>
                                        <th-btn :sorted-column="messagesSortedColumn" column="accuracy" sortable
                                                title="Accuracy" @sort="messagesSort"/>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="message in messages"
                                        v-if="!messagesProcessing && messages?.length"
                                        :key="message.id"
                                        class="text-left">
                                        <td>{{ message.id }}</td>
                                        <td>{{ formatDateTime(message.event_time) }}</td>
                                        <td>{{ message.trigger_event && message.trigger_event.toLowerCase() !== 'unknown' ? strOperations().formatEnumString(message.trigger_event) : '' }}
                                        </td>
                                        <td>{{ strOperations().unitifySpeed(message) }}</td>
                                        <td>{{ strOperations().formatVoltage(message.voltage1) }}</td>
                                        <td>{{ message.rssi }}</td>
                                        <td>{{ `${message.latitude} , ${message.longitude}` }}</td>
                                        <td>{{ message.satellites }}</td>
                                        <td>{{ message.accuracy }}</td>
                                    </tr>
                                    <tr v-else>
                                        <td v-if="messagesProcessing" colspan="9">
                                            <v-progress-circular color="primary" indeterminate/>
                                        </td>
                                        <td v-else colspan="9">No messages found</td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <v-pagination v-if="messagesTotalPages > 1" v-model="messagesCurrentPage"
                                              :length="messagesTotalPages"
                                              class="py-0 pt-2"
                                              density="compact"
                                              @click="fetchMessages"/>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 3">
                                <v-table density="compact" hover>
                                    <thead>
                                    <tr>
                                        <th-btn :sorted-column="logsSortedColumn" column="raw_packet_id"
                                                sortable title="Packet ID" @sort="logsSort"/>
                                        <th-btn :sorted-column="logsSortedColumn" column="received_time"
                                                sortable title="Timestamp" @sort="logsSort"/>
                                        <th>Asset</th>
                                        <th-btn :sorted-column="logsSortedColumn" column="direction"
                                                sortable title="Direction" @sort="logsSort"/>
                                        <th-btn :sorted-column="logsSortedColumn" column="packet_type"
                                                sortable title="Packet Type" @sort="logsSort"/>
                                        <th>Payload</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(log , index) in logs"
                                        v-if="!logsProcessing && logs.length"
                                        :key="log.id" class="text-left">
                                        <td>{{ log.raw_packet_id }}</td>
                                        <td>{{ formatDateTime(log.received_time) }}</td>
                                        <td>{{ log.asset_label }}</td>
                                        <td>{{ log.direction }}</td>
                                        <td>{{ strOperations().formatEnumString(log.packet_type) }}</td>
                                        <td class="cursor-pointer">
                                            <v-btn
                                                variant="text"
                                                icon="mdi-information"
                                                @click="showPayload(log)"
                                            />
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td v-if="logsProcessing" colspan="6">
                                            <v-progress-circular color="primary" indeterminate/>
                                        </td>
                                        <td v-else colspan="6">No message log found</td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <v-pagination v-if="logsTotalPages > 1"
                                              v-model="logsCurrentPage" :length="logsTotalPages"
                                              class="py-0 pt-2"
                                              density="compact"
                                              @click="fetchLogs"/>
                            </v-card-text>

                            <v-card-text v-if="activeTab === 4">
                                <v-table density="compact" hover>
                                    <thead>
                                    <tr>
                                        <th>Asset</th>
                                        <th-btn :sorted-column="queuesSortedColumn" column="queued_time" sortable
                                                title="Queued Time" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="expiry_time" sortable
                                                title="Expiry Tme" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="last_transmit_time" sortable
                                                title="Last Transmit" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="transmit_time" sortable
                                                title="Transmit Time" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="max_transmit_attempts"
                                                sortable
                                                title="Max Attempts" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="command" sortable
                                                title="Command" @sort="queuesSort"/>
                                        <th>Payload (HEX)</th>
                                        <th-btn :sorted-column="queuesSortedColumn" column="total_transmit_attempts"
                                                sortable
                                                title="Total Attempts" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="completed" sortable
                                                title="Completed" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="user_comment" sortable
                                                title="Notes" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="deleted_at" sortable
                                                title="Deleted At" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="created_at" sortable
                                                title="Created At" @sort="queuesSort"/>
                                        <th-btn :sorted-column="queuesSortedColumn" column="updated_at" sortable
                                                title="Updated At" @sort="queuesSort"/>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="queue in queues"
                                        v-if="!queuesProcessing && queues.length"
                                        :key="queue.id" class="text-left">
                                        <td>{{ queue.asset?.label }}</td>
                                        <td>{{ formatDateTime(queue.queued_time) }}</td>
                                        <td>{{ formatDateTime(queue.expiry_time) }}</td>
                                        <td>{{ formatDateTime(queue.last_transmit_time) }}</td>
                                        <td>{{ formatDateTime(queue.transmit_time) }}</td>
                                        <td>{{ queue.max_transmit_attempts }}</td>
                                        <td>{{ queue.payload }}</td>
                                        <td>{{ queue.total_transmit_attempts }}</td>
                                        <td>{{ queue.completed ? 'True' : 'False' }}</td>
                                        <td>{{ queue.user_comment }}</td>
                                        <td>{{ formatDateTime(queue.deleted_at) }}</td>
                                        <td>{{ formatDateTime(queue.created_at) }}</td>
                                        <td>{{ formatDateTime(queue.updated_at) }}</td>
                                    </tr>
                                    <tr v-else>
                                        <td v-if="queuesProcessing" colspan="13">
                                            <v-progress-circular color="primary" indeterminate/>
                                        </td>
                                        <td v-else colspan="13">No outgoing queue found</td>
                                    </tr>
                                    </tbody>
                                </v-table>

                                <v-pagination v-if="queuesTotalPages > 1"
                                              v-model="queuesCurrentPage" :length="queuesTotalPages"
                                              class="py-0 pt-2"
                                              density="compact"
                                              @click="fetchQueues"/>
                            </v-card-text>
                        </v-card>
                    </v-tabs-window-item>
                </v-tabs-window>
            </v-col>
        </v-row>
    </v-container>


    <v-dialog v-model="logDialogSwitch" max-width="700" scrollable>
        <v-card title="Message Payload">
            <v-card-text>
                <pre>{{ selectedLog.message_payload }}</pre>
            </v-card-text>

            <v-card-actions></v-card-actions>
        </v-card>
    </v-dialog>

    <edit-device v-if="editDialogSwitch" v-model="editDialogSwitch" :device="device" @updateDevice="onDeviceUpdated"/>
    <message-create-edit v-if="commandDialogSwitch" v-model="commandDialogSwitch" :device="device"/>
    <ota-status v-if="otaStatusDialogSwitch" v-model="otaStatusDialogSwitch" :device="device"/>
    <service-pin v-if="servicePinDialogSwitch && user.super" v-model="servicePinDialogSwitch" :device="device"/>
</template>

<script setup>
import {alertColor, alertIcon, alertLabel} from '@/composables/alertTypes';
import {computed, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import {strOperations} from "@/composables/globals";
import {formatDateTime} from "@/composables/useTimezone";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useClipboard} from "@/composables/clipboard";
import EditDevice from "@/components/dialogs/devices/Edit.vue";
import MessageCreateEdit from "@/components/dialogs/devices/commands/Create_Edit.vue";
import OtaStatus from "@/components/dialogs/devices/OtaStatus.vue";
import ServicePin from "@/components/dialogs/devices/ServicePin.vue";
import {LMap, LTileLayer, LMarker, LIcon} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";

const router = useRouter();
const {user} = useAuthStore();
const editDialogSwitch = ref(false);
const commandDialogSwitch = ref(false);
const otaStatusDialogSwitch = ref(false);
const servicePinDialogSwitch = ref(false);
const device = ref(null);
const {showSnackbar} = useSnackbarStore();
const {copyToClipboard} = useClipboard();

const locationMap = ref(null);
const onLocationMapReady = () => {
    if (locationMap.value) locationMap.value.leafletObject.attributionControl?.remove();
};
const openGoogleMaps = () => {
    const lat = device.value?.device_status?.latitude;
    const lng = device.value?.device_status?.longitude;
    if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
};
const serialNumber = router.currentRoute.value.params.slno;

const otaLabel = (status) => {
    const labels = { 1: 'Queued', 2: 'Queued', 3: 'Acknowledged', 4: 'Downloading', 5: 'Downloaded', 6: 'Failed', 7: 'Upgraded' };
    return labels[status] || `Status ${status}`;
};
const otaColor = (status) => {
    if (status === 7) return 'success';
    if (status === 6) return 'error';
    if (status >= 3 && status <= 5) return 'info';
    return 'grey';
};
const deviceStatuses = [
    'Active',
    'Disabled',
    'Faulty',
    'Lost'
];

const activeTab = ref(0);
const tabs = [
    {value: 0, title: 'Notifications'},
    {value: 1, title: 'Firmware / OTA'},
    {value: 2, title: 'Trip Messages'},
];
if (user.type === 'admin') {
    tabs.push(
        {value: 3, title: 'All Messages'},
        {value: 4, title: 'Outgoing Queue'},
    )
}

const fixTypes = {
    0: 'None',
    1: 'None',
    2: '2D',
    3: '3D'
}

const subscriptionDaysLeft = (endDate) => {
    if (!endDate) return null;
    const diff = Math.ceil((new Date(endDate) - new Date()) / 86400000);
    return diff;
};
const subscriptionChipColor = (endDate) => {
    const days = subscriptionDaysLeft(endDate);
    if (days === null) return 'grey';
    if (days < 0) return 'error';
    if (days <= 30) return 'warning';
    return 'success';
};
const subscriptionChipLabel = (endDate) => {
    const days = subscriptionDaysLeft(endDate);
    if (days === null) return 'No Expiry';
    if (days < 0) return 'Expired';
    if (days === 0) return 'Expires today';
    if (days <= 30) return `Expires in ${days}d`;
    return 'Active';
};
const subscriptionExpiryClass = (endDate) => {
    const days = subscriptionDaysLeft(endDate);
    if (days === null) return '';
    if (days < 0) return 'text-error';
    if (days <= 30) return 'text-warning';
    return '';
};
const LocationSource = {
    0: 'GPS',
    1: 'LTE',
    2: 'Beacon',
    3: 'Gateway'
}
/* device_table removed - replaced by 4-column info panel in template */
const deviceDetails = computed(() => {
    return {
        ...device.value,
        status: deviceStatuses[device.value?.status],
        signal_bars: device.value?.device_status?.signal_bars,
        voltage1: strOperations().formatVoltage(device.value?.device_status?.voltage1),
        voltage2: strOperations().formatVoltage(device.value?.device_status?.voltage2),
        device_status: {
            ...device.value?.device_status,
            voltage1: strOperations().formatVoltage(device.value?.device_status?.voltage1),
            voltage2: strOperations().formatVoltage(device.value?.device_status?.voltage2),
            gps_speed: strOperations().unitifyDistance(device.value?.device_status?.gps_speed),
            obd_speed: strOperations().unitifyDistance(device.value?.device_status?.obd_speed)
        }
    };
});
onMounted(async () => {
    await send('get', `devices/${serialNumber}/details`)
        .then((response) => {
            device.value = response.data.data.device;
        })
        .catch((error) => {
            showSnackbar(error.response.data.message ?? 'Device could not be loaded', 'error')
        });
});

const onDeviceUpdated = (updatedDevice) => {
    device.value = updatedDevice;
    editDialogSwitch.value = false;
};

const repairLicense = async () => {
    if (!confirm(`Queue license repair for device ${device.value.slno}?\n\nThis will write a new license key and reboot the device.`)) return;
    try {
        const response = await send('post', `devices/${device.value.id}/repair-license`);
        if (response?.data?.success) {
            showSnackbar(`License repair queued. License: ${response.data.data.license}`, 'success');
        } else {
            showSnackbar(response?.data?.message || 'Failed to queue license repair', 'error');
        }
    } catch (e) {
        showSnackbar(e.response?.data?.message || e.message, 'error');
    }
};

const openMessageLogs = () => {
    // Always device_id: the Message Log filter is the device picker now,
    // so linking by asset_id would apply a filter with no visible control
    // and no way to clear it.
    const query = {device_id: device.value?.id};
    router.push({name: 'messageLog', query});
};

/***************
 *** Methods ***
 ***************/
const updateTab = () => {
    switch (activeTab.value) {
        case 0:
            if (!notifications.value.length)
                fetchNotifications()
            break;
        case 1:
            /* Firmware/OTA tab - data loaded with device, no extra fetch needed */
            break;
        case 2:
            if (!messages.value.length)
                fetchMessages()
            break;
        case 3:
            if (!logs.value.length)
                fetchLogs()
            break;
        case 4:
            if (!queues.value.length)
                fetchQueues()
            break;
    }
}

/*******************************
 *** Notifications Tab Start ***
 *******************************/

const notifications = ref([]);
const notificationsTotalPages = ref(1);
const notificationsCurrentPage = ref(1);
const notificationsProcessing = ref(false);
const fetchNotifications = () => {
    if (!device.value.asset_id) {
        notifications.value = [];
        return;
    }
    notificationsProcessing.value = true;
    send('get', `assets/notifications`, {
        asset: device.value.asset_id,
        page: notificationsCurrentPage.value,
    })
        .then((response) => {
            notifications.value = response.data.data.data ?? [];
            notificationsTotalPages.value = response.data.data.last_page;
            notificationsCurrentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            showSnackbar(error.response.data.message ?? 'Notifications could not be loaded', 'error')
        }).finally(() => notificationsProcessing.value = false);
}

/*****************************
 *** Notifications Tab End ***
 *****************************/

/*******************************
 *** Messages Tab Start ***
 *******************************/

const messages = ref([]);
const messagesTotalPages = ref(1);
const messagesCurrentPage = ref(1);
const messagesSortedType = ref(null);
const messagesProcessing = ref(false);
const messagesSortedColumn = ref(null);
const messagesSort = (sorting) => {
    messagesSortedColumn.value = sorting.column;
    messagesSortedType.value = sorting.order;
    messagesCurrentPage.value = 1
    fetchMessages();
}
const fetchMessages = () => {
    if (!device.value.asset_id) {
        messages.value = [];
        return;
    }
    messagesProcessing.value = true;
    const data = {
        rows: 10,
        page: messagesCurrentPage.value,
        order: messagesSortedType.value,
        sort: messagesSortedColumn.value,
        columns: JSON.stringify("id,message_type,trigger_event,event_time,gps_speed,voltage1,rssi,latitude,longitude,satellites,accuracy")
    };
    send('get', `assets/${device.value.asset_id}/trips-messages`, data)
        .then((response) => {
            messages.value = response.data.data.data ?? [];
            messagesTotalPages.value = response.data.data.meta.last_page;
            messagesCurrentPage.value = response.data.data.meta.current_page;
        })
        .catch((error) => {
            showSnackbar(error.response.data.message ?? 'Messages could not be loaded', 'error')
        }).finally(() => messagesProcessing.value = false);
}

/************************
 *** Messages Tab End ***
 ************************/

/*******************************
 *** Raw Message Logs Tab Start ***
 *******************************/

const logs = ref([]);
const selectedLog = ref(null);
const logsTotalPages = ref(1);
const logsCurrentPage = ref(1);
const logsSortedType = ref(null);
const logsProcessing = ref(false);
const logDialogSwitch = ref(false);
const logsSortedColumn = ref(null);

const logsSort = (sorting) => {
    logsSortedColumn.value = sorting.column;
    logsSortedType.value = sorting.order;
    logsCurrentPage.value = 1
    fetchLogs();
}
const fetchLogs = () => {
    logsProcessing.value = true;
    send('get', `devices/message-logs`, {
        sort: logsSortedColumn.value,
        order: logsSortedType.value,
        page: logsCurrentPage.value,
        device_id: device.value.id,
        devices: true
    }).then((response) => {
        logs.value = response.data.data.data.data;
        logsTotalPages.value = response.data.data.data.meta.last_page;
        logsCurrentPage.value = response.data.data.data.meta.current_page;
    }).catch((error) => {
        showSnackbar(error.response.data.message ?? 'Message Logs could not be loaded', 'error')
    }).finally(() => logsProcessing.value = false);
}
const showPayload = (message) => {
    if (message.packet_type === 'LOCATION_UPDATE') {
        message.message_payload['payload']['trigger_source'] = message['trigger_source']
    }
    selectedLog.value = message;
    logDialogSwitch.value = true
}

/*****************************
 *** Raw Message Logs Tab End ***
 *****************************/

/*******************************
 *** Outgoing Queues Tab Start ***
 *******************************/

const queues = ref([]);
const queuesSortedType = ref(null);
const queuesSortedColumn = ref(null);
const queuesTotalPages = ref(1);
const queuesCurrentPage = ref(1);
const queuesProcessing = ref(false);
const queuesSort = (sorting) => {
    queuesSortedColumn.value = sorting.column;
    queuesSortedType.value = sorting.order;
    queuesCurrentPage.value = 1
    fetchQueues();
}
const fetchQueues = () => {
    const data = {
        devices: true,
        device_id: device.value.id,
        page: queuesCurrentPage.value,
        order: queuesSortedType.value,
        sort: queuesSortedColumn.value,
    };
    queuesProcessing.value = true;
    send('get', `devices/downlink-queues`, data)
        .then((response) => {
            queues.value = response.data.data.data.data;
            queuesTotalPages.value = response.data.data.data.last_page;
            queuesCurrentPage.value = response.data.data.data.current_page;
        })
        .catch((error) => {
            showSnackbar(error.response.data.message ?? 'Outgoing Queues could not be loaded', 'error')
        }).finally(() => queuesProcessing.value = false);
}

/*****************************
 *** Outgoing Queues Tab End ***
 *****************************/
</script>

<style scoped>
pre {
    color: #ccc;
    padding: 5px;
    background: #2d2d2d;
    white-space: pre-wrap;
}

.v-tab.v-tab.v-btn {
    background-color: #fbfbfb;
    border-right: 1px solid #e7e7e7;
    border-bottom: 1px solid #e7e7e7;
}

.v-tab.v-tab.v-btn.v-tab-item--selected {
    background-color: #F5F5F5 !important;
}
</style>
